import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Wedding } from '@/lib/types';
import { api } from '@/lib/api';
import { getWeddingId, setWedding, clearWedding } from '@/lib/storage';

// Fetch weddingId from IndexedDB
async function fetchWeddingId(): Promise<string | null> {
  return getWeddingId();
}

// Query key factories
export const weddingKeys = {
  id: ['weddingId'] as const,
  detail: (id: string) => ['wedding', id] as const,
};

// Hook to get the stored wedding ID
export function useWeddingId() {
  return useQuery({
    queryKey: weddingKeys.id,
    queryFn: fetchWeddingId,
    staleTime: Infinity,
  });
}

// Hook to fetch wedding data
export function useWedding(weddingId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: weddingKeys.detail(weddingId ?? ''),
    queryFn: async () => {
      const wedding = await api.get<Wedding>(`/api/weddings/${weddingId}`);
      await setWedding(wedding);
      return wedding;
    },
    enabled: !!weddingId,
    staleTime: 30_000,
    retry: false,
    // On error, clear stale stored data so auto-create kicks in
    meta: {
      onError: async () => {
        await clearWedding();
        queryClient.setQueryData(weddingKeys.id, null);
      },
    },
  });
}

// Mutation: create a new wedding
export function useCreateWedding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { timezone: string; coupleNames?: string }) =>
      api.post<Wedding>('/api/weddings', data),
    onSuccess: async (wedding) => {
      await setWedding(wedding);
      queryClient.setQueryData(weddingKeys.id, wedding.id);
      queryClient.setQueryData(weddingKeys.detail(wedding.id), wedding);
    },
  });
}

// Mutation: update wedding details
export function useUpdateWedding(weddingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Pick<Wedding, 'coupleNames' | 'weddingDate' | 'venue' | 'timezone'>>) =>
      api.put<Wedding>(`/api/weddings/${weddingId}`, data),
    onSuccess: async (wedding) => {
      await setWedding(wedding);
      queryClient.setQueryData(weddingKeys.detail(weddingId), wedding);
    },
  });
}

// Hook that auto-creates a wedding on first load if none exists
export function useInitWedding() {
  const queryClient = useQueryClient();
  const { data: weddingId, isLoading: idLoading } = useWeddingId();
  const createWedding = useCreateWedding();

  useEffect(() => {
    if (idLoading || weddingId || createWedding.isPending || createWedding.isSuccess) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    createWedding.mutate({ timezone }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: weddingKeys.id });
      },
    });
  }, [weddingId, idLoading, createWedding, queryClient]);

  return {
    weddingId: weddingId ?? null,
    isCreating: createWedding.isPending,
    initialized: createWedding.isSuccess || !!weddingId,
  };
}
